import { useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { supabase } from '../lib/supabase';
import { Calendar, Loader2, PlayCircle, PowerOff, Trash2, Wrench, Upload, X, ChevronDown, Send } from 'lucide-react';
import { format } from 'date-fns';
import { Portal } from '../components/common/Portal';
import { LiveEventInlineCard } from '../components/community/LiveEventInlineCard';

export type LiveEventRow = {
  id: string;
  title: string;
  description: string | null;
  event_date: string; // ISO
  event_end_date: string | null; // ISO
  thumbnail_url: string | null;
  event_url: string | null;
  is_started: boolean | null;
  is_ended: boolean | null;
  replay_available: boolean | null;
};

function useNow(tickMs = 1000) {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), tickMs);
    return () => clearInterval(t);
  }, [tickMs]);
  return now;
}

// formatDuration removed (replaced by Countdown component)

export default function LiveEventsUser() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<LiveEventRow[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const now = useNow(1000);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LiveEventRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [endDialog, setEndDialog] = useState<LiveEventRow | null>(null);
  const [form, setForm] = useState({ title: '', description: '', link: '', date: '', time: '', endTime: '' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [joinModal, setJoinModal] = useState<LiveEventRow | null>(null);
  const [joinEmbedFallback, setJoinEmbedFallback] = useState(false);
  const [calendarMenu, setCalendarMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  // Share-to-chat state
  const [shareEvent, setShareEvent] = useState<LiveEventRow | null>(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareCategoryId, setShareCategoryId] = useState<string | ''>('');
  // Edit replay link modal
  const [editReplay, setEditReplay] = useState<LiveEventRow | null>(null);
  const [editReplayLink, setEditReplayLink] = useState('');
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  // Timezone helpers: store input as Pacific Time and persist ISO in UTC
  const getTimeZoneOffsetMinutes = (date: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).formatToParts(date);
    const filled: Record<string, string> = {};
    for (const p of parts) filled[p.type] = p.value;
    const zoned = new Date(Date.UTC(
      Number(filled.year), Number(filled.month) - 1, Number(filled.day),
      Number(filled.hour), Number(filled.minute), Number(filled.second)
    ));
    return (zoned.getTime() - date.getTime()) / 60000;
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_categories')
        .select('id, name')
        .order('name');
      if (error) throw error;
      setCategories(data || []);
      if ((data?.length || 0) > 0 && !shareCategoryId) setShareCategoryId(data![0].id);
    } catch (e) {
      console.error('Failed to fetch categories', e);
    }
  };

  const handleShareToChat = async () => {
    if (!shareEvent) return;
    try {
      setShareSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const content = `${shareMessage?.trim() || ''}\n[event:${shareEvent.id}]`.trim();
      const { error } = await supabase
        .from('messages')
        .insert({
          content,
          user_id: user.id,
          like_count: 0,
          category_id: shareCategoryId || null,
        });
      if (error) throw error;
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-2 rounded-lg backdrop-blur-md shadow-lg text-sm font-medium';
      toast.textContent = 'Shared to chat!';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      setShareEvent(null);
      setShareMessage('');
    } catch (e: any) {
      console.error(e);
      alert(e?.message || 'Failed to share to chat');
    } finally {
      setShareSubmitting(false);
    }
  };

  // Outlook.com / Office 365 compose deeplink
  const buildOutlookCalendarUrl = (ev: LiveEventRow) => {
    try {
      const start = new Date(ev.event_date);
      const end = ev.event_end_date ? new Date(ev.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000);
      const isoNoMs = (d: Date) => d.toISOString().slice(0, 19);
      const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        startdt: isoNoMs(start),
        enddt: isoNoMs(end),
        subject: ev.title || 'Live Event',
        body: ev.description ? `${ev.description}\n\nLink: ${ev.event_url || ''}` : `Link: ${ev.event_url || ''}`,
        location: 'Online',
      });
      // For consumer accounts:
      const live = `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
      // For O365 accounts fallback (many will redirect appropriately):
      const office = `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
      return { live, office };
    } catch {
      return { live: 'https://outlook.live.com/calendar/', office: 'https://outlook.office.com/calendar/' };
    }
  };

  // ICS generation and download for Apple/Outlook
  const icsEscape = (s: string) => s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => `\\${m}`);
  const buildIcs = (ev: LiveEventRow) => {
    const start = new Date(ev.event_date);
    const end = ev.event_end_date ? new Date(ev.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000);
    const summary = icsEscape(ev.title || 'Live Event');
    const descriptionRaw = ev.description ? `${ev.description}\n\nLink: ${ev.event_url || ''}` : `Link: ${ev.event_url || ''}`;
    const description = icsEscape(descriptionRaw);
    const dtstamp = toGoogleDate(new Date());
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//BetterCommunity//LiveEvents//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${ev.id}@bettercommunity`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${toGoogleDate(start)}`,
      `DTEND:${toGoogleDate(end)}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `URL:${ev.event_url || ''}`,
      'LOCATION:Online',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
  };
  const downloadIcs = (ev: LiveEventRow, filenameHint = 'event') => {
    const content = buildIcs(ev);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = (ev.title || filenameHint).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || filenameHint;
    a.href = url;
    a.download = `${name}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Build Google Calendar add-event URL from UTC ISO event_date
  const toGoogleDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
  };
  const buildGoogleCalendarUrl = (ev: LiveEventRow) => {
    try {
      const start = new Date(ev.event_date);
      const end = ev.event_end_date ? new Date(ev.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000); // default 1h
      const dates = `${toGoogleDate(start)}/${toGoogleDate(end)}`;
      const params = new URLSearchParams({
        text: ev.title || 'Live Event',
        dates,
        details: ev.description ? `${ev.description}\n\nLink: ${ev.event_url || ''}` : `Link: ${ev.event_url || ''}`,
        location: 'Online',
      });
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&${params.toString()}`;
    } catch {
      return 'https://calendar.google.com/calendar/';
    }
  };

  const toISOInTimeZone = (dateStr: string, timeStr: string, timeZone: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    const base = new Date(Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0));
    const offsetMin = getTimeZoneOffsetMinutes(base, timeZone);
    const utcMs = base.getTime() - offsetMin * 60000;
    return new Date(utcMs).toISOString();
  };

  const toFormInTimeZone = (iso: string, timeZone: string) => {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(d);
    const by: Record<string, string> = {};
    for (const p of parts) by[p.type] = p.value;
    const date = `${by.year}-${by.month}-${by.day}`;
    const time = `${by.hour}:${by.minute}`;
    return { date, time };
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Check admin status
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: prof } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .maybeSingle();
          const adminFlag = prof?.is_admin === true || user.email === 'markofilipovic2003@gmail.com';
          setIsAdmin(adminFlag);
          if (adminFlag) await fetchCategories();
        } else {
          setIsAdmin(false);
        }
        await refreshEvents();
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When opening the join modal, show a fallback link if the iframe can't load (X-Frame-Options)
  useEffect(() => {
    if (joinModal) {
      setJoinEmbedFallback(false);
      const t = setTimeout(() => setJoinEmbedFallback(true), 2000);
      return () => clearTimeout(t);
    } else {
      setJoinEmbedFallback(false);
    }
  }, [joinModal]);

  // Close calendar dropdown with Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCalendarMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const refreshEvents = async () => {
    const { data, error } = await supabase
      .from('live_events')
      .select('id, title, description, event_date, event_end_date, thumbnail_url, event_url, is_started, is_ended, replay_available')
      .order('event_date', { ascending: true });
    if (error) throw error;
    const visible = (data as LiveEventRow[]).filter((ev) => !(ev.is_ended && !ev.replay_available));
    setEvents(visible);
  };

  // No thumbnail derivation: admins must provide thumbnail_url

  const sections = useMemo(() => {
    const upcoming: LiveEventRow[] = [];
    const live: LiveEventRow[] = [];
    const late: LiveEventRow[] = [];
    const ended: LiveEventRow[] = [];

    for (const ev of events) {
      const start = new Date(ev.event_date);
      const hasStarted = !!ev.is_started && !ev.is_ended;
      const isEnded = !!ev.is_ended;
      if (hasStarted) {
        live.push(ev);
      } else if (isEnded && ev.replay_available) {
        ended.push(ev);
      } else if (!isEnded && now.getTime() < start.getTime()) {
        upcoming.push(ev);
      } else if (!isEnded && now.getTime() >= start.getTime() && !hasStarted) {
        late.push(ev);
      }
    }

    return { upcoming, live, late, ended };
  }, [events, now]);

  // Upcoming: live first, then scheduled chronologically (late + upcoming)
  const upcomingList = useMemo(() => {
    const scheduled = [...sections.late, ...sections.upcoming].sort(
      (a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
    );
    return [...sections.live, ...scheduled];
  }, [sections.live, sections.late, sections.upcoming]);

  // Past: ended with replay (most recent first)
  const pastList = useMemo(() => {
    return [...sections.ended].sort(
      (a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime()
    );
  }, [sections.ended]);

  // Utility to build embed URL (StreamYard and others)
  const buildStreamyardEmbed = (url?: string | null): string | null => {
    if (!url) return null;
    try {
      const u = new URL(url);
      // Only handle StreamYard specially
      if (u.hostname.includes('streamyard.com')) {
        const path = u.pathname || '/';
        // Case 1: Watch URLs already work – keep current behavior
        if (path.startsWith('/watch/')) {
          u.searchParams.set('embed', 'true');
          return u.toString();
        }
        // Case 2: Already the embed variant /e/<id>
        if (path.startsWith('/e/')) {
          u.searchParams.set('embed', 'true');
          return u.toString();
        }
        // Case 3: Plain event URL https://streamyard.com/<id>
        const id = path.replace(/^\//, '').split('/')[0];
        if (id) {
          // Construct the official embed URL form
          const embedUrl = new URL(`https://streamyard.com/e/${id}`);
          // Optional: request embed mode (harmless if ignored)
          embedUrl.searchParams.set('embed', 'true');
          return embedUrl.toString();
        }
      }
      // Non-StreamYard – keep previous minimal behavior
      u.searchParams.set('embed', 'true');
      return u.toString();
    } catch {
      return url;
    }
  };

  // Fancy countdown component (days/hours/mins/secs)
  function Countdown({ target }: { target: Date }) {
    const [nowTick, setNowTick] = useState(() => Date.now());
    useEffect(() => {
      const t = setInterval(() => setNowTick(Date.now()), 1000);
      return () => clearInterval(t);
    }, []);
    const diff = Math.max(0, target.getTime() - nowTick);
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const Box = ({ label, value }: { label: string; value: number }) => (
      <div className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-lg px-2 py-1 min-w-[54px]">
        <div className="text-[#c9fffc] text-lg font-bold tracking-wider">
          {String(value).padStart(2, '0')}
        </div>
        <div className="text-[10px] uppercase text-gray-400">{label}</div>
      </div>
    );
    return (
      <div className="flex items-center gap-2">
        <Box label="Days" value={days} />
        <span className="text-gray-500">:</span>
        <Box label="Hours" value={hours} />
        <span className="text-gray-500">:</span>
        <Box label="Mins" value={minutes} />
        <span className="text-gray-500">:</span>
        <Box label="Secs" value={seconds} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-white mb-6">Live Events</h1>
        <div className="text-white flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin"/> Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-white mb-6">Live Events</h1>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  // Even if no events, render page to allow admin to create

  // Admin helpers: modal, upload, submit, end actions
  const resetForm = () => {
    setForm({ title: '', description: '', link: '', date: '', time: '', endTime: '' });
    setEditing(null);
    setFile(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (ev: LiveEventRow) => {
    setEditing(ev);
    const { date, time } = toFormInTimeZone(ev.event_date, 'America/Los_Angeles');
    // Derive end time; default to +1h if missing
    const endISO = ev.event_end_date || new Date(new Date(ev.event_date).getTime() + 60 * 60 * 1000).toISOString();
    const endParts = toFormInTimeZone(endISO, 'America/Los_Angeles');
    setForm({
      title: ev.title,
      description: ev.description || '',
      link: ev.event_url || '',
      date,
      time,
      endTime: endParts.time,
    });
    setFile(null);
    setShowForm(true);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }
    setFile(f);
  };

  const uploadCover = async (): Promise<string | null> => {
    if (!file) return editing?.thumbnail_url || null;
    const ext = file.name.split('.').pop();
    const key = `cover-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: upErr } = await supabase
      .storage
      .from('event-thumbnails')
      .upload(key, file, { upsert: false, contentType: file.type });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from('event-thumbnails').getPublicUrl(key);
    return data.publicUrl;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      if (!form.date || !form.time) throw new Error('Please set date and time');
      const whenISO = toISOInTimeZone(form.date, form.time, 'America/Los_Angeles');
      const endISO = form.endTime
        ? toISOInTimeZone(form.date, form.endTime, 'America/Los_Angeles')
        : new Date(new Date(whenISO).getTime() + 60 * 60 * 1000).toISOString();
      const payload: any = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_date: whenISO,
        event_end_date: endISO,
        event_url: form.link.trim() || null,
        user_id: user.id,
      };
      const coverUrl = await uploadCover();
      if (coverUrl) payload.thumbnail_url = coverUrl;
      if (editing) {
        const { error } = await supabase.from('live_events').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        if (!coverUrl) throw new Error('Cover image is required');
        const { error } = await supabase.from('live_events').insert(payload);
        if (error) throw error;
      }
      await refreshEvents();
      setShowForm(false);
      resetForm();
    } catch (e: any) {
      alert(e?.message || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndWithReplay = async (ev: LiveEventRow) => {
    const { error } = await supabase
      .from('live_events')
      .update({ is_ended: true, is_started: false, replay_available: true, ended_at: new Date().toISOString() })
      .eq('id', ev.id);
    if (!error) await refreshEvents();
    setEndDialog(null);
  };

  const handleEndNoReplay = async (ev: LiveEventRow) => {
    const { error } = await supabase.from('live_events').delete().eq('id', ev.id);
    if (!error) await refreshEvents();
    setEndDialog(null);
  };

  const renderCard = (ev: LiveEventRow) => {
    const start = new Date(ev.event_date);
    // msTil not needed; Countdown shows detailed timer
    const stateLive = !!ev.is_started && !ev.is_ended;
    const stateEnded = !!ev.is_ended;

    return (
      <div key={ev.id} className="bg-[#0d1117]/80 border border-white/10 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition">
        <div className="flex flex-col md:flex-row gap-4 p-4">
          {/* Media: embed for past replays, image otherwise */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-gray-700">
              {ev.is_ended && ev.replay_available && ev.event_url ? (
                <iframe
                  src={buildStreamyardEmbed(ev.event_url) || ev.event_url}
                  className="w-full h-full"
                  frameBorder={0}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title={`Replay: ${ev.title}`}
                />
              ) : ev.thumbnail_url ? (
                <img src={ev.thumbnail_url} alt={ev.title} className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Calendar className="w-10 h-10 text-gray-500"/></div>
              )}

      {/* Share-to-chat Modal */}
      {isAdmin && shareEvent && (
        <Portal>
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShareEvent(null)}>
            <div className="absolute inset-0 bg-black/70" />
            <div className="relative w-full max-w-2xl rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6 z-10" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white text-lg font-semibold">Share to chat</h3>
                <button onClick={() => setShareEvent(null)} className="text-gray-300 hover:text-white">✕</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Message</label>
                  <textarea value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} rows={3} className="w-full bg-white/5 border border-white/10 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" placeholder="Write something about this event..." />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Category</label>
                  <select value={shareCategoryId} onChange={(e) => setShareCategoryId(e.target.value)} className="w-full bg-white/5 border border-white/10 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]">
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Preview</label>
                  <LiveEventInlineCard event={shareEvent} />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShareEvent(null)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Cancel</button>
                  <button disabled={shareSubmitting} onClick={handleShareToChat} className="px-4 py-2 bg-[#c9fffc] text-gray-900 rounded font-medium hover:bg-[#a0fcf9] disabled:opacity-50 inline-flex items-center gap-2">
                    <Send className="w-4 h-4"/> {shareSubmitting ? 'Sharing...' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

              {/* Mobile overlay Add-to-Calendar (scheduled only) */}
              {!stateLive && !stateEnded && (
                <div className="absolute top-2 right-2 z-10 block sm:hidden">
                  <button
                    onClick={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      const rect = el.getBoundingClientRect();
                      const width = 192;
                      const top = rect.bottom + window.scrollY + 8;
                      const left = Math.max(8, rect.right + window.scrollX - width);
                      setCalendarMenu({ id: ev.id, top, left });
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/40 backdrop-blur border border-white/15 text-white text-xs hover:bg-black/50"
                  >
                    <Calendar className="w-3.5 h-3.5"/> Add <ChevronDown className="w-3.5 h-3.5 opacity-70"/>
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-amber-300">Event</span>
              {stateLive ? (
                <span className="relative inline-flex items-center gap-1 text-xs text-red-300">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  Live now
                </span>
              ) : stateEnded ? (
                activeTab === 'past' ? null : <span className="text-xs text-gray-400">Event ended</span>
              ) : (
                <span className="text-xs text-gray-300">Scheduled</span>
              )}
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white">{ev.title}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4"/>
                <span>{format(start, 'EEE, MMM d')}</span>
                <span className="text-gray-400">{`${format(start, 'h:mm a')} - ${format(ev.event_end_date ? new Date(ev.event_end_date) : new Date(start.getTime() + 60 * 60 * 1000), 'h:mm a')}`}</span>
              </div>
              <div className="hidden sm:block text-gray-400">Online</div>
            </div>
            {ev.description && <p className="text-gray-300 overflow-hidden text-ellipsis">{ev.description}</p>}

            {/* Actions */}
            <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
              {stateLive ? (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => setJoinModal(ev)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${ev.event_url ? 'bg-[#c9fffc] text-gray-900 hover:bg-[#a0fcf9]' : 'bg-gray-700 text-gray-300 cursor-not-allowed'}`}
                    disabled={!ev.event_url}
                  >
                    <PlayCircle className="w-5 h-5"/> Join Now
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => { setShareEvent(ev); setShareMessage(''); }} className="px-3 py-2 bg-[#c9fffc] text-gray-900 rounded hover:bg-[#a0fcf9] inline-flex items-center gap-2">
                        <Send className="w-4 h-4"/> Share
                      </button>
                      <button onClick={() => setEndDialog(ev)} className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 inline-flex items-center gap-2">
                        <PowerOff className="w-4 h-4"/> End
                      </button>
                      <button onClick={() => openEdit(ev)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2">
                        <Wrench className="w-4 h-4"/> Manage
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm('Delete this event?')) return;
                          const { error } = await supabase.from('live_events').delete().eq('id', ev.id);
                          if (!error) await refreshEvents();
                        }}
                        className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-500 inline-flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4"/> Delete
                      </button>
                    </>
                  )}
                </div>
              ) : !stateEnded ? (
                <>
                  <div className="ml-auto flex items-center gap-3">
                    {isAdmin && (
                      <>
                        <button
                          onClick={async () => {
                            const { error } = await supabase
                              .from('live_events')
                              .update({ is_started: true, is_ended: false, replay_available: false, started_at: new Date().toISOString() })
                              .eq('id', ev.id);
                            if (!error) await refreshEvents();
                          }}
                          className="px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-500 inline-flex items-center gap-2"
                        >
                          <PlayCircle className="w-4 h-4"/> Start
                        </button>
                        <button onClick={() => openEdit(ev)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2">
                          <Wrench className="w-4 h-4"/> Manage
                        </button>
                        <button onClick={() => { setShareEvent(ev); setShareMessage(''); }} className="px-3 py-2 bg-[#c9fffc] text-gray-900 rounded hover:bg-[#a0fcf9] inline-flex items-center gap-2">
                          <Send className="w-4 h-4"/> Share
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm('Delete this event?')) return;
                            const { error } = await supabase.from('live_events').delete().eq('id', ev.id);
                            if (!error) await refreshEvents();
                          }}
                          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-500 inline-flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4"/> Delete
                        </button>
                      </>
                    )}
                    {/* Add to Calendar dropdown - left of the timer (desktop/tablet only) */}
                    <div className="relative hidden sm:block">
                      <button
                        onClick={(e) => {
                          const el = e.currentTarget as HTMLElement;
                          const rect = el.getBoundingClientRect();
                          const width = 192; // menu width
                          const top = rect.bottom + window.scrollY + 8;
                          const left = Math.max(8, rect.right + window.scrollX - width);
                          setCalendarMenu({ id: ev.id, top, left });
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10"
                      >
                        <Calendar className="w-4 h-4"/> Add to Calendar <ChevronDown className="w-4 h-4 opacity-70"/>
                      </button>
                    </div>
                    <Countdown target={start} />
                  </div>
                </>
              ) : (
                activeTab === 'past' && isAdmin ? (
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => { setEditReplay(ev); setEditReplayLink(ev.event_url || ''); }}
                      className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2"
                    >
                      Manage Replay Link
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('Delete this past event?')) return;
                        const { error } = await supabase.from('live_events').delete().eq('id', ev.id);
                        if (!error) await refreshEvents();
                      }}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-500 inline-flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4"/> Delete
                    </button>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">Live Events</h1>
        {isAdmin && (
          <button onClick={openCreate} className="px-4 py-2 rounded-lg bg-[#c9fffc] text-gray-900 font-semibold hover:bg-[#a0fcf9] inline-flex items-center gap-2">
            <Upload className="w-4 h-4"/> Create Event
          </button>
        )}
      </div>

      {/* Tabs similar to Community style */}
      <div className="mb-6 flex items-center">
        <div className="bg-[#151D29]/40 backdrop-blur-md p-1 rounded-lg border border-[#c9fffc]/10 shadow-inner inline-flex">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-1.5 text-sm rounded-lg transition-all ${activeTab === 'upcoming' ? 'bg-[#c9fffc] text-[#151D29] font-medium' : 'text-[#c9fffc]/80 hover:text-[#c9fffc]'}`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-4 py-1.5 text-sm rounded-lg transition-all ${activeTab === 'past' ? 'bg-[#c9fffc] text-[#151D29] font-medium' : 'text-[#c9fffc]/80 hover:text-[#c9fffc]'}`}
          >
            Past
          </button>
        </div>
      </div>

      {activeTab === 'upcoming' ? (
        <div className="mb-10 space-y-4">
          {upcomingList.length > 0 ? (
            <div className="space-y-4">
              {upcomingList.map(renderCard)}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">No upcoming events.</div>
          )}
        </div>
      ) : (
        <div className="mb-6 space-y-4">
          {pastList.length > 0 ? (
            <div className="space-y-4">
              {pastList.map(renderCard)}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">No past events.</div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isAdmin && showForm && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowForm(false)} />
            <div className="relative w-full max-w-xl rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
              <button className="absolute top-3 right-3 text-gray-400 hover:text-white" onClick={() => setShowForm(false)}>
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-white mb-4">{editing ? 'Edit Event' : 'Create Event'}</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Title</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white min-h-[80px]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Date</label>
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">Time</label>
                    <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">End Time (optional)</label>
                  <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                  <div className="text-xs text-gray-400 mt-1">If left empty, the event will default to 1 hour long. Times are saved in Pacific Time (PT).</div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Event Link</label>
                  <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Thumbnail</label>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white" />
                  {editing?.thumbnail_url && !file && (
                    <div className="mt-2 text-xs text-gray-400">Current image will be kept if you don't upload a new one.</div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button onClick={() => setShowForm(false)} className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">Cancel</button>
                <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 rounded-lg bg-[#c9fffc] text-gray-900 font-semibold hover:bg-[#a0fcf9] disabled:opacity-60">
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Calendar dropdown rendered in a Portal to avoid clipping */}
      {calendarMenu && (
        <Portal>
          <div className="fixed inset-0 z-[60]" onClick={() => setCalendarMenu(null)}>
            <div
              className="absolute"
              style={{ top: `${calendarMenu.top}px`, left: `${calendarMenu.left}px`, width: 192 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rounded-lg border border-white/10 bg-[#0d1117] shadow-xl">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/5"
                  onClick={() => {
                    window.open(buildGoogleCalendarUrl(events.find(e => e.id === calendarMenu.id)!), '_blank', 'noopener,noreferrer');
                    setCalendarMenu(null);
                  }}
                >
                  Google Calendar
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/5"
                  onClick={() => {
                    const ev = events.find(e => e.id === calendarMenu.id);
                    if (ev) downloadIcs(ev, 'apple-calendar');
                    setCalendarMenu(null);
                  }}
                >
                  Apple Calendar (.ics)
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/5"
                  onClick={() => {
                    const ev = events.find(e => e.id === calendarMenu.id);
                    if (ev) {
                      const u = buildOutlookCalendarUrl(ev);
                      const win = window.open(u.live, '_blank', 'noopener,noreferrer');
                      if (!win) window.open(u.office, '_blank', 'noopener,noreferrer');
                    } else {
                      window.open('https://outlook.live.com/calendar/', '_blank', 'noopener,noreferrer');
                    }
                    setCalendarMenu(null);
                  }}
                >
                  Outlook
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Join modal - embed StreamYard join page */}
      {joinModal && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setJoinModal(null)} />
            <div className="relative w-full max-w-5xl aspect-video bg-black border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <button
                className="absolute top-3 right-3 z-10 text-gray-300 hover:text-white"
                onClick={() => setJoinModal(null)}
              >
                <X className="w-5 h-5" />
              </button>
              {!joinEmbedFallback && (
                <iframe
                  src={joinModal.event_url || ''}
                  className="w-full h-full"
                  allow="camera; microphone; autoplay; fullscreen; clipboard-write"
                  frameBorder={0}
                  onLoad={() => setJoinEmbedFallback(false)}
                  title={`Join: ${joinModal.title}`}
                />
              )}
              {joinEmbedFallback && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70 text-gray-200 p-6 text-center">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Preparing the join page…</span>
                  </div>
                  <p className="text-sm text-gray-300">If it doesn’t load, StreamYard may block embedding. Use the button below.</p>
                  <a
                    href={joinModal.event_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c9fffc] text-gray-900 font-semibold hover:bg-[#a0fcf9]"
                  >
                    Open in new tab
                  </a>
                </div>
              )}
            </div>
          </div>
        </Portal>
      )}

      {/* End dialog */}
      {isAdmin && endDialog && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setEndDialog(null)} />
            <div className="relative w-full max-w-md rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
              <button className="absolute top-3 right-3 text-gray-400 hover:text-white" onClick={() => setEndDialog(null)}>
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-white mb-2">End live event</h3>
              <p className="text-gray-300 mb-4">Choose how you want to end this event.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => handleEndWithReplay(endDialog)} className="flex-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500">End with replay</button>
                <button onClick={() => handleEndNoReplay(endDialog)} className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500">End (no replay)</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Edit Replay Link Modal */}
      {isAdmin && editReplay && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setEditReplay(null)} />
            <div className="relative w-full max-w-md rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl p-6">
              <button className="absolute top-3 right-3 text-gray-400 hover:text-white" onClick={() => setEditReplay(null)}>
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-white mb-2">Edit Replay Link</h3>
              <p className="text-gray-300 text-sm mb-3">Update the URL used for the replay. Supports YouTube/StreamYard/etc.</p>
              <input
                value={editReplayLink}
                onChange={(e) => setEditReplayLink(e.target.value)}
                placeholder="https://..."
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
              />
              <div className="mt-5 flex items-center justify-end gap-2">
                <button onClick={() => setEditReplay(null)} className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">Cancel</button>
                <button
                  onClick={async () => {
                    try {
                      const url = editReplayLink.trim();
                      if (url && !/^https?:\/\//i.test(url)) {
                        alert('Please enter a valid URL (must start with http or https)');
                        return;
                      }
                      const { error } = await supabase
                        .from('live_events')
                        .update({ event_url: url || null })
                        .eq('id', editReplay.id);
                      if (error) throw error;
                      await refreshEvents();
                      setEditReplay(null);
                    } catch (e: any) {
                      alert(e?.message || 'Failed to update replay link');
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-[#c9fffc] text-gray-900 font-semibold hover:bg-[#a0fcf9]"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
